import { MOODS_CONSTANT, MOODS_MIXPANEL_CONSTANT } from '@libs/constants';
import { useQuery } from '@tanstack/react-query';
import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EVENT } from '../../contexts/MixpanelConstants';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import { moodsConfigurationQueryOptions } from '../../hooks/useMoodsQuery';
import { IStore } from '../../interfaces/StoreTypes';
import { fetchUserChannelsList } from '../../store/reducers/liveTvSlice';
import { resetMoodState, setMoodType } from '../../store/reducers/moodsRecommendationSlice';
import { __ } from '../../translations';
import { CarouselWrapper, LiveTV, Lucky, OnDemand } from '../elements/moods';
import { getListing, getLoader } from '../elements/moods/elements/getListing';

export const MoodsRecommendation = React.memo((props: any) => {
    const { track } = useContext(MixpanelContext);
    const dispatch = useDispatch();
    const moodTypeState = useSelector((state: IStore) => state.moodsRecommendation.moodType);
    const { channels } = useSelector((state: IStore) => state.liveTv.userChannels) ?? [];

    // Use React Query for mood configuration data
    const {
        data: content = [],
        isLoading: configLoading,
        error: configError,
    } = useQuery(moodsConfigurationQueryOptions());

    // Derived state - automatically recalculates when moodTypeState or content changes
    const selectedMood = moodTypeState && content ? content.find((mood) => mood.id === moodTypeState) : null;

    const moodClickHandler = (id) => {
        const mood = content.find((el) => el.id === id);
        dispatch(setMoodType(mood?.id || null));

        // Mixpanel Event
        track(EVENT.MOODS.MOOD_TYPE_SELECT, {
            'Mood Type': MOODS_MIXPANEL_CONSTANT[mood?.type],
        });
    };

    const changeMoodHandler = () => {
        dispatch(resetMoodState());
    };

    // Ensure channels are loaded
    useEffect(() => {
        if (!channels || channels.length === 0) {
            dispatch(fetchUserChannelsList(null));
        }
    }, [channels, dispatch]);

    // Show loading state while configuration is loading
    if (configLoading) {
        return <CarouselWrapper title={__('mood_recommendation_categories_title')}>{getLoader(4)}</CarouselWrapper>;
    }

    // Show error state if configuration failed to load
    if (configError) {
        return (
            <CarouselWrapper title={__('mood_recommendation_categories_title')}>
                <div>Failed to load mood recommendations</div>
            </CarouselWrapper>
        );
    }

    let Component;
    switch (selectedMood?.type) {
        case MOODS_CONSTANT.liveTV:
            Component = <LiveTV mood={selectedMood} onChangeMood={changeMoodHandler} />;
            break;
        case MOODS_CONSTANT.lucky:
            Component = <Lucky mood={selectedMood} onChangeMood={changeMoodHandler} />;
            break;
        case MOODS_CONSTANT.tvshows:
        case MOODS_CONSTANT.movies:
            Component = <OnDemand mood={selectedMood} onChangeMood={changeMoodHandler} />;
            break;
        default:
            Component = (
                <CarouselWrapper title={__('mood_recommendation_categories_title')}>
                    {content.length === 0 ? getLoader(4) : getListing(content, moodClickHandler)}
                </CarouselWrapper>
            );
            break;
    }

    return Component;
});
